import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useUser } from '../state/UserContext';
import { CSS_VAR_NAMES, DEFAULT_THEME_ID, THEMES, type ThemeId, type ThemeTokens } from './tokens';

interface ThemeContextValue {
  themeId: ThemeId;
  tokens: ThemeTokens;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function applyTokensToRoot(tokens: ThemeTokens) {
  const root = document.documentElement;
  (Object.keys(tokens) as (keyof ThemeTokens)[]).forEach((key) => {
    root.style.setProperty(CSS_VAR_NAMES[key], tokens[key]);
  });
}

/**
 * Applies the logged-in user's `themePreference` (a direct choice, independent of
 * `traditions[]`) as CSS custom properties on the root element. Every user defaults to
 * `saffron` until they pick something else in Settings.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { me } = useUser();
  const themeId: ThemeId = (me?.themePreference as ThemeId | undefined) ?? DEFAULT_THEME_ID;
  const tokens = useMemo(() => THEMES[themeId] ?? THEMES[DEFAULT_THEME_ID], [themeId]);

  useEffect(() => {
    applyTokensToRoot(tokens);
    document.documentElement.setAttribute('data-theme', themeId);
  }, [tokens, themeId]);

  const value: ThemeContextValue = useMemo(() => ({ themeId, tokens }), [themeId, tokens]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
